// #15 Phase C — "ARTWORKS ON THIS LAND" (Parcel Profile discoverability).
//
// Lists the ACTIVE Frozen Artworks whose canonical Placement physically
// occupies / intersects THIS parcel's rect. Membership is authoritative
// geometry (backend `public/placements?x,y,w,h`) — NEVER derived from
// creator/owner. An Artwork spanning multiple adjacent parcels legitimately
// appears in each parcel it physically occupies. Clicking an entry switches
// the contextual selection to the Artwork Profile. No Marketplace / Sell /
// Transfer / Burn controls (out of #15 scope).

import { useEffect, useState } from "react";
import { artworkApi, type PublicPlacement } from "../lib/artwork-api";
import { useI18n } from "../i18n";
import { PlacedArtworkCanvas } from "../viewer/GridArtworkLayer";
import { ARTWORK_SELECT_EVENT } from "../viewer/GridArtworkLayer";

function thumbBox(w: number, h: number, max = 40): { boxW: number; boxH: number } {
  if (w <= 0 || h <= 0) return { boxW: max, boxH: max };
  const k = max / Math.max(w, h);
  return { boxW: Math.max(1, Math.round(w * k)), boxH: Math.max(1, Math.round(h * k)) };
}

export function ParcelArtworks({
  parcel,
}: {
  parcel: { id: number; x: number; y: number; w: number; h: number };
}) {
  const { t } = useI18n();
  const [items, setItems] = useState<PublicPlacement[] | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const ac = new AbortController();
    setItems(null);
    setFailed(false);
    artworkApi
      .publicPlacements(parcel.x, parcel.y, parcel.w, parcel.h, ac.signal)
      .then((r) => setItems(r.placements))
      .catch((e) => {
        if (ac.signal.aborted || (e as Error)?.name === "AbortError") return;
        setFailed(true);
      });
    return () => ac.abort();
  }, [parcel.id, parcel.x, parcel.y, parcel.w, parcel.h]);

  const select = (p: PublicPlacement) => {
    window.dispatchEvent(
      new CustomEvent(ARTWORK_SELECT_EVENT, {
        detail: { artworkId: p.artwork_id, anchorX: p.anchor_x, anchorY: p.anchor_y },
      }),
    );
  };

  // Never surface the section as "empty=0" while still loading (would be a lie).
  if (failed) return null;

  const count = items?.length ?? 0;

  return (
    <div className="flex flex-col gap-2" data-testid="parcel-artworks-section">
      <div className="flex items-center justify-between">
        <span className="font-mono text-[8.5px] uppercase tracking-[0.32em] text-text-muted">
          {t("grid.artworks.title")}
        </span>
        {items !== null && (
          <span
            data-testid="parcel-artworks-count"
            className="font-mono text-[10px] tabular-nums"
            style={{ color: count > 0 ? "var(--spec-cyan)" : "var(--text-muted)" }}
          >
            {count}
          </span>
        )}
      </div>

      {items === null && (
        <span data-testid="parcel-artworks-loading" className="font-mono text-[10px] text-text-muted">
          {t("grid.artworks.scanning")}
        </span>
      )}

      {items !== null && count === 0 && (
        <span data-testid="parcel-artworks-empty" className="text-[11px] leading-relaxed text-text-muted">
          {t("grid.artworks.empty")}
        </span>
      )}

      {items !== null && count > 0 && (
        <ul className="flex flex-col gap-1.5">
          {items.map((p) => {
            const { boxW, boxH } = thumbBox(p.width, p.height);
            return (
              <li key={p.placement_id}>
                <button
                  type="button"
                  data-testid={`parcel-artwork-card-${p.artwork_id}`}
                  onClick={() => select(p)}
                  className="group flex w-full items-center gap-3 rounded-md border border-[color:var(--spec-hairline)] bg-white/[0.02] px-2 py-1.5 text-left transition-colors hover:border-[color:var(--spec-cyan)]"
                >
                  <span
                    className="flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-sm bg-black/40"
                    aria-hidden
                  >
                    <PlacedArtworkCanvas
                      content={p.content}
                      width={p.width}
                      height={p.height}
                      boxW={boxW}
                      boxH={boxH}
                    />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate font-display text-[13px] font-semibold text-text-primary group-hover:text-[color:var(--spec-cyan)]">
                      {p.title?.trim() || t("grid.artworks.untitled")}
                    </span>
                    <span className="block font-mono text-[10px] tabular-nums text-text-muted">
                      {p.width}×{p.height} · #{String(p.artwork_id).slice(0, 8)}
                    </span>
                  </span>
                  <span className="font-mono text-[9px] uppercase tracking-[0.18em] text-text-muted group-hover:text-[color:var(--spec-cyan)]">
                    {t("grid.artworks.view")}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
